<template>
  <!-- MODAL OVERLAY — Teleport al body para que position: fixed funcione siempre -->
  <Teleport to="body">
    <div v-if="visible" class="cm-overlay" @click.self="cerrar">
      <transition name="cm-slide" appear>
        <div class="cm-modal" role="dialog" aria-modal="true" aria-labelledby="cm-title">

          <!-- HEADER DEL MODAL -->
          <div class="cm-header">
            <div class="cm-header-info">
              <div class="cm-avatar">
                <img src="@/assets/logo/extragas-logo.png" alt="">
              </div>
              <div class="cm-header-text">
                <strong id="cm-title">{{ empresa.nombre }}</strong>
                <small>Asistente · en línea</small>
              </div>
            </div>

            <div class="cm-header-actions">
              <button
                type="button"
                class="cm-icon-btn"
                @click="reiniciar"
                title="Empezar de nuevo"
              >
                <Icon icon="mdi:refresh" />
              </button>
              <button
                type="button"
                class="cm-icon-btn cm-close"
                @click="cerrar"
                title="Cerrar"
              >
                <Icon icon="mdi:close" />
              </button>
            </div>
          </div>

          <!-- CUERPO — EL CHAT -->
          <div class="cm-body">

            <!-- MENSAJES -->
            <div class="cm-chat" ref="chatRef" aria-live="polite"></div>

            <!-- BOTONES DE OPCIÓN -->
            <div v-if="tipoEsperado === 'botones' && botonesOpciones.length" class="cm-options">
              <button
                v-for="(op, i) in botonesOpciones"
                :key="i"
                type="button"
                class="cm-option-btn"
                @click="tocarBoton(op)"
              >
                {{ op.label }}
              </button>
            </div>

            <!-- BARRA DE ESCRITURA -->
            <form
              v-if="tipoEsperado === 'texto' || tipoEsperado === 'ambos'"
              class="cm-input-bar"
              @submit.prevent="enviarMensaje"
            >
              <input
                v-model="textoEscrito"
                type="text"
                class="cm-input-txt"
                :placeholder="placeholderActual"
                autocomplete="off"
                ref="inputRef"
                :disabled="!esperandoRespuesta"
              />
              <button
                type="submit"
                class="cm-input-send"
                :disabled="!textoEscrito.trim() || !esperandoRespuesta"
                aria-label="Enviar"
              >
                <Icon icon="mdi:send" class="text-base" />
              </button>
            </form>

            <!-- MODO MIXTO -->
            <div v-if="tipoEsperado === 'ambos' && botonesOpciones.length" class="cm-options cm-options-inline">
              <button
                v-for="(op, i) in botonesOpciones"
                :key="i"
                type="button"
                class="cm-option-btn cm-option-inline"
                @click="tocarBoton(op)"
              >
                {{ op.label }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </Teleport>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { obtenerTiposRecarga, buscarClientePorCedula } from '@/api/gas.js';
import { obtenerDatosEmpresa } from '@/utils/tenantConfig';

export default {
  name: 'ChatbotModal',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      seq: 0,
      modo: null,
      saving: false,
      tiposRecarga: [],
      carrito: [],
      clienteEncontrado: null,
      textoEscrito: '',
      placeholderActual: 'Escribe un mensaje…',
      esperandoRespuesta: false,
      botonesOpciones: [],
      resolverActual: null,
      tipoEsperado: 'texto',
      empresa: {
        nombre: 'EXTRA GAS',
        rif: 'J-41023119-0',
        direccion: 'C.C. LATIN CENTER PB LOCAL 01',
        ciudad: 'ACARIGUA - PORTUGUESA'
      },
      form: {
        nombre: '', apellido: '', cedula: '', telefono: '', email: '',
        direccion: '', urlMapa: '', puntoReferencia: '', tipoCliente: 'residencial'
      }
    };
  },
  computed: {
    visible() {
      return this.modelValue;
    },
    totalGeneralBs() {
      return this.carrito.reduce((s, i) => {
        return s + Number(i.precioBs || 0) * Number(i.cantidad || 0);
      }, 0);
    }
  },
  watch: {
    modelValue(nuevo) {
      const bodyScroll = document.getElementById('body-scroll');
      if (nuevo) {
        document.body.style.overflow = 'hidden';
        if (bodyScroll) bodyScroll.style.overflow = 'hidden';
        this.$nextTick(() => {
          if (this.tiposRecarga.length === 0) {
            this.cargarTipos().then(() => this.reiniciar());
          } else {
            this.reiniciar();
          }
        });
      } else {
        document.body.style.overflow = '';
        if (bodyScroll) bodyScroll.style.overflow = '';
      }
    }
  },
  async mounted() {
    this.cargarEmpresa();
    await this.cargarTipos();
  },
  beforeUnmount() {
    document.body.style.overflow = '';
    const bodyScroll = document.getElementById('body-scroll');
    if (bodyScroll) bodyScroll.style.overflow = '';
  },
  methods: {
    cargarEmpresa() {
      try {
        const d = obtenerDatosEmpresa();
        if (d) {
          this.empresa = {
            nombre: d.nombreComercial || d.nombre || this.empresa.nombre,
            rif: d.rif || this.empresa.rif,
            direccion: d.direccion || this.empresa.direccion,
            ciudad: d.ciudad || this.empresa.ciudad
          };
        }
      } catch (e) { /* noop */ }
    },

    async cargarTipos() {
      try {
        const res = await obtenerTiposRecarga('activos');
        if (res?.success === true) {
          this.tiposRecarga = Array.isArray(res.data) ? res.data : [];
        }
      } catch (e) {
        console.error('Error cargando tipos:', e);
        this.tiposRecarga = [];
      }
    },

    cerrar() {
      this.$emit('update:modelValue', false);
    },

    scrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatRef;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        const inp = this.$refs.inputRef;
        if (inp && this.esperandoRespuesta && this.tipoEsperado !== 'botones') inp.focus();
      });
    },

    msg(texto, who = 'in') {
      const el = document.createElement('div');
      el.className = 'cm-msg ' + who;
      el.innerHTML = texto;
      this.$refs.chatRef.appendChild(el);
      this.scrollBottom();
      return el;
    },

    bot(texto, delay = 700) {
      const my = this.seq;
      return new Promise((resolve) => {
        const t = document.createElement('div');
        t.className = 'cm-typing';
        t.innerHTML = '<i></i><i></i><i></i>';
        this.$refs.chatRef.appendChild(t);
        this.scrollBottom();
        setTimeout(() => {
          t.remove();
          if (my !== this.seq) return resolve();
          this.msg(texto);
          resolve();
        }, delay);
      });
    },

    esperarRespuesta(opts = {}) {
      return new Promise((resolve) => {
        this.placeholderActual = opts.placeholder || 'Escribe un mensaje…';
        this.botonesOpciones = opts.botones || [];
        this.tipoEsperado = opts.tipo || 'texto';
        this.esperandoRespuesta = true;
        this.resolverActual = resolve;
        this.scrollBottom();
      });
    },

    enviarMensaje() {
      const txt = (this.textoEscrito || '').trim();
      if (!txt || !this.esperandoRespuesta || !this.resolverActual) return;
      this.msg(this.escapar(txt), 'out');
      this.textoEscrito = '';
      const resolver = this.resolverActual;
      this.resolverActual = null;
      this.esperandoRespuesta = false;
      this.botonesOpciones = [];
      resolver(txt);
    },

    tocarBoton(op) {
      if (!this.esperandoRespuesta || !this.resolverActual) return;
      this.msg(this.escapar(op.label), 'out');
      const resolver = this.resolverActual;
      this.resolverActual = null;
      this.esperandoRespuesta = false;
      this.botonesOpciones = [];
      resolver(op.value);
    },

    escapar(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },

    async reiniciar() {
      this.seq++;
      this.modo = null;
      this.saving = false;
      this.carrito = [];
      this.clienteEncontrado = null;
      this.form = {
        nombre: '', apellido: '', cedula: '', telefono: '', email: '',
        direccion: '', urlMapa: '', puntoReferencia: '', tipoCliente: 'residencial'
      };
      this.textoEscrito = '';
      this.esperandoRespuesta = false;
      this.botonesOpciones = [];
      this.resolverActual = null;
      if (this.$refs.chatRef) this.$refs.chatRef.innerHTML = '';

      await this.bot('¡Hola! 👋 Soy el asistente de <b>' + this.empresa.nombre + '</b> 🔥');
      await this.bot('Te ayudo a pedir tu gas a domicilio. ¿Empezamos?', 500);

      const r = await this.esperarRespuesta({
        botones: [{ label: '🚀 Empezar', value: 'si' }],
        tipo: 'botones'
      });

      if (r === 'si') await this.preguntarModo();
    },

    async preguntarModo() {
      await this.bot('¿Eres cliente nuevo o ya has pedido antes?');
      const r = await this.esperarRespuesta({
        botones: [
          { label: '🆕 Soy cliente nuevo', value: 'nuevo' },
          { label: '🔍 Ya soy cliente', value: 'existente' }
        ],
        tipo: 'botones'
      });
      if (r === 'nuevo') {
        this.modo = 'nuevo';
        await this.flujoNuevo();
      } else {
        this.modo = 'existente';
        await this.flujoExistente();
      }
    },

    async flujoExistente() {
      await this.bot('¡Perfecto! Dime tu cédula y la busco en el sistema.');
      const cedula = await this.pedirCedula();

      await this.bot('🔍 Buscando en la base de datos…', 500);
      try {
        const res = await buscarClientePorCedula(cedula);
        if (res?.success && res.data) {
          const raw =
            res.data?.cliente ??
            res.data?.Cliente ??
            res.data;

          if (raw && (raw.nombre || raw.Nombre)) {
            this.clienteEncontrado = raw;
            this.form = {
              nombre: raw.nombre || raw.Nombre || '',
              apellido: raw.apellido || raw.Apellido || '',
              cedula: raw.cedula || raw.Cedula || cedula,
              telefono: raw.telefono || raw.Telefono || '',
              email: raw.email || raw.Email || '',
              direccion: raw.direccion || raw.Direccion || '',
              urlMapa: raw.urlMapa || raw.UrlMapa || '',
              puntoReferencia: raw.puntoReferencia || raw.PuntoReferencia || '',
              tipoCliente: raw.tipoCliente || raw.TipoCliente || 'residencial'
            };

            await this.bot('✅ <b>¡Ya te encontré, ' + this.form.nombre + '!</b>');

            const totalPedidos =
              raw.totalPedidos ??
              raw.TotalPedidos ??
              raw.total_pedidos ??
              0;

            if (totalPedidos > 0) {
              await this.bot(
                'Veo que has pedido <b>' + totalPedidos + '</b> ' +
                (totalPedidos === 1 ? 'vez' : 'veces') + ' antes. 🙌',
                500
              );
            }

            const nombreZona =
              raw.nombreZona || raw.NombreZona || raw.zona || null;

            if (nombreZona) {
              await this.bot(
                '📍 Tu zona de entrega es: <b>' + nombreZona + '</b>\n' +
                'Usaremos esta zona para tu pedido. 👍',
                500
              );
            }

            await this.confirmarDatosGuardados();
            await this.confirmarDireccion();
            await this.pedirServicios();
            await this.confirmarYEnviar();
            return;
          }
        }
      } catch (e) {
        console.error(e);
      }

      this.clienteEncontrado = null;
      await this.bot('😕 No encontré esa cédula en el sistema.');
      const r = await this.esperarRespuesta({
        botones: [
          { label: '🆕 Registrarme como nuevo', value: 'nuevo' },
          { label: '🔁 Intentar otra cédula', value: 'retry' }
        ],
        tipo: 'botones'
      });
      if (r === 'retry') return this.flujoExistente();
      this.modo = 'nuevo';
      await this.flujoNuevo();
    },

    async confirmarDatosGuardados() {
      await this.bot(
        'Estos son los datos que tengo guardados:\n' +
        '📞 ' + this.form.telefono + '\n' +
        '📧 ' + this.form.email + '\n' +
        '¿Están correctos?'
      );
      const r = await this.esperarRespuesta({
        botones: [
          { label: '✅ Sí, están bien', value: 'si' },
          { label: '✏️ Quiero cambiarlos', value: 'no' }
        ],
        tipo: 'botones'
      });
      if (r === 'no') {
        await this.bot('¿Cuál es tu nuevo teléfono?');
        this.form.telefono = await this.pedirTelefono();
        await this.bot('¿Y tu nuevo email?');
        this.form.email = await this.pedirEmail();
        await this.bot('✅ Datos actualizados.');
      } else {
        await this.bot('Genial. 👍');
      }
    },

    async confirmarDireccion() {
      if (!this.form.direccion) {
        await this.bot('No tengo tu dirección guardada. ¿Me la das?');
        this.form.urlMapa = '';
        this.form.puntoReferencia = '';
        await this.pedirDireccionCompleta();
        return;
      }

      await this.bot('📍 Entregamos en:\n<b>' + this.form.direccion + '</b>\n¿Está bien?');
      const r = await this.esperarRespuesta({
        botones: [
          { label: '✅ Sí, ahí mismo', value: 'si' },
          { label: '✏️ Cambiar dirección', value: 'no' }
        ],
        tipo: 'botones'
      });

      if (r === 'no') {
        this.form.direccion = '';
        this.form.urlMapa = '';
        this.form.puntoReferencia = '';
        await this.bot('Ok, actualicemos tu dirección. 📝');
        await this.pedirDireccionCompleta();
      } else {
        // ⭐ Aunque la dirección ya esté guardada, si no hay urlMapa se pide
        if (!this.form.urlMapa || !String(this.form.urlMapa).toLowerCase().includes('http')) {
          await this.bot('⚠️ Necesito la ubicación exacta en el mapa para la entrega.');
          await this.pedirUbicacionObligatoria();
        } else {
          await this.bot('Perfecto. 📍');
        }
      }
    },

    // =====================================================
    // ⭐ FLUJO CLIENTE NUEVO — verificación de cédula robusta
    // =====================================================
    async flujoNuevo() {
      await this.bot('¡Excelente! Vamos a crear tu cuenta. 🚀');

      await this.bot('¿Cuál es tu nombre?');
      const nombre = await this.pedirNombre();
      this.form.nombre = nombre;

      await this.bot('Gracias, ' + nombre + '. ¿Y tu apellido?', 500);
      const apellido = await this.pedirApellido();
      this.form.apellido = apellido;

      await this.bot('Encantado, ' + nombre + ' ' + apellido + ' 👋');

      // ⭐ BUCLE: pedir cédula y verificar si ya existe ANTES de continuar
      while (true) {
        await this.bot('Ahora dime tu cédula.', 500);
        const cedula = await this.pedirCedula();

        await this.bot('🔍 Verificando si ya estás registrado…', 400);

        let existente = null;
        try {
          const res = await buscarClientePorCedula(cedula);
          // console.log('buscarClientePorCedula →', res); // depuración

          if (res && res.success === true) {
            const raw =
              res.data?.cliente ??
              res.data?.Cliente ??
              res.data;

            if (raw && (raw.nombre || raw.Nombre)) {
              existente = raw;
            }
          }
        } catch (e) {
          console.error('Error verificando cédula:', e);
        }

        if (existente) {
          this.clienteEncontrado = existente;
          this.form.cedula = existente.cedula || existente.Cedula || cedula;

          const nombreMostrar =
            (existente.nombre || existente.Nombre || '').trim();
          const apellidoMostrar =
            (existente.apellido || existente.Apellido || '').trim();
          const nombreCompleto = (nombreMostrar + ' ' + apellidoMostrar).trim();

          await this.bot(
            '⚠️ <b>Esta cédula ya está registrada</b>' +
            (nombreCompleto ? ' a nombre de <b>' + nombreCompleto + '</b>' : '') +
            '.\n\nNo puedes crear una cuenta nueva con esta cédula.',
            600
          );

          const decision = await this.esperarRespuesta({
            botones: [
              { label: '✅ Continuar como ese cliente', value: 'usar' },
              { label: '🔁 Probar otra cédula', value: 'otra' }
            ],
            tipo: 'botones'
          });

          if (decision === 'otra') {
            this.clienteEncontrado = null;
            this.form.cedula = '';
            continue;
          }

          this.modo = 'existente';
          this.form = {
            nombre: nombreMostrar || this.form.nombre,
            apellido: apellidoMostrar || this.form.apellido,
            cedula: existente.cedula || existente.Cedula || cedula,
            telefono: existente.telefono || existente.Telefono || '',
            email: existente.email || existente.Email || '',
            direccion: existente.direccion || existente.Direccion || '',
            urlMapa: existente.urlMapa || existente.UrlMapa || '',
            puntoReferencia: existente.puntoReferencia || existente.PuntoReferencia || '',
            tipoCliente: existente.tipoCliente || existente.TipoCliente || 'residencial'
          };

          const nombreZona =
            existente.nombreZona ||
            existente.NombreZona ||
            existente.zona ||
            null;

          if (nombreZona) {
            await this.bot('📍 Tu zona de entrega es: <b>' + nombreZona + '</b>', 500);
          }

          await this.bot('Perfecto, sigo con tus datos guardados. 👍', 500);
          await this.confirmarDatosGuardados();
          await this.confirmarDireccion();
          await this.pedirServicios();
          await this.confirmarYEnviar();
          return;
        }

        this.form.cedula = cedula;
        await this.bot('✅ Cédula disponible. Continuemos. 👍', 400);
        break;
      }

      await this.bot('¿Cuál es tu número de teléfono?');
      this.form.telefono = await this.pedirTelefono();

      await this.bot('¿Y tu correo electrónico?');
      this.form.email = await this.pedirEmail();

      await this.pedirDireccionCompleta();

      await this.bot('¿Qué tipo de cliente eres?');
      const tipo = await this.esperarRespuesta({
        botones: [
          { label: '🏠 Residencial', value: 'residencial' },
          { label: '🏢 Comercial', value: 'comercial' },
          { label: '🏭 Industrial', value: 'industrial' }
        ],
        tipo: 'botones'
      });
      this.form.tipoCliente = tipo;

      await this.pedirServicios();
      await this.confirmarYEnviar();
    },

    // =====================================================
    // ⭐ DIRECCIÓN + UBICACIÓN OBLIGATORIA (sin omitir)
    // =====================================================
    async pedirDireccionCompleta() {
      await this.bot('Dime la dirección donde te entregamos el gas.');
      const dir = await this.esperarRespuesta({
        placeholder: 'Ej: Calle 5 con Av. 8, Casa 12',
        tipo: 'texto'
      });
      if (dir.length < 5) {
        await this.bot('Escribe una dirección más completa por favor.');
        return this.pedirDireccionCompleta();
      }
      this.form.direccion = dir;

      // ⭐ Ubicación OBLIGATORIA (sin opción de omitir)
      await this.pedirUbicacionObligatoria();

      await this.bot('¿Algún punto de referencia para el repartidor?');
      const ref = await this.esperarRespuesta({
        placeholder: 'Ej: Frente a la panadería',
        botones: [{ label: '⏭️ Omitir', value: 'omitir' }],
        tipo: 'ambos'
      });
      this.form.puntoReferencia = ref === 'omitir' ? '' : ref;
    },

    // ⭐ Método auxiliar: pide ubicación hasta obtenerla (GPS o link)
    async pedirUbicacionObligatoria() {
      await this.bot('📍 ¿Cómo me das la ubicación exacta?');
      await this.bot('⚠️ La ubicación es <b>obligatoria</b> para poder entregarte el gas.', 400);

      const opcion = await this.esperarRespuesta({
        botones: [
          { label: '📡 Usar mi ubicación actual', value: 'gps' },
          { label: '📋 Pegar link de Google Maps', value: 'link' }
        ],
        tipo: 'botones'
      });

      if (opcion === 'gps') {
        const ok = await this.obtenerUbicacionGPS();
        if (!ok) {
          await this.bot('Necesito que me compartas la ubicación por link de Maps para continuar.');
          await this.pedirLinkMapa();
        }
      } else {
        await this.pedirLinkMapa();
      }

      // ⭐ Validación final: debe existir urlMapa con http
      while (!this.form.urlMapa || !String(this.form.urlMapa).toLowerCase().includes('http')) {
        await this.bot('⚠️ Falta la ubicación. Es obligatoria para continuar.');
        await this.pedirLinkMapa();
      }
    },

    async obtenerUbicacionGPS() {
      if (!navigator.geolocation) {
        await this.bot('😕 Tu navegador no permite ubicación automática. Usemos el link de Maps.');
        return false;
      }

      if (!window.isSecureContext) {
        await this.bot('⚠️ La ubicación automática solo funciona en sitios seguros (HTTPS). Usemos el link de Maps.');
        return false;
      }

      await this.bot('📡 Pidiendo permiso al navegador…');
      await this.bot('💡 Cuando aparezca el aviso, toca <b>"Permitir"</b>. Asegúrate de tener el GPS del celular encendido.', 500);

      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0
          });
        });

        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const precision = Math.round(pos.coords.accuracy);

        this.form.urlMapa = `https://www.google.com/maps?q=${lat},${lng}`;

        let direccionTexto = '';
        try {
          direccionTexto = await this.reverseGeocoding(lat, lng);
        } catch (e) { /* silencioso */ }

        if (direccionTexto) {
          if (!this.form.direccion || this.form.direccion.length < 10) {
            this.form.direccion = direccionTexto;
          }
          await this.bot(
            '✅ <b>¡Ubicación capturada!</b>\n' +
            '📍 ' + direccionTexto + '\n' +
            '🎯 Precisión: ±' + precision + ' m'
          );
        } else {
          await this.bot(
            '✅ <b>¡Ubicación capturada!</b>\n' +
            '📍 Coordenadas: ' + lat.toFixed(6) + ', ' + lng.toFixed(6) + '\n' +
            '🎯 Precisión: ±' + precision + ' m'
          );
        }

        if (precision > 100) {
          await this.bot('⚠️ La señal no fue muy precisa. Si puedes, activa el GPS del teléfono y vuelve a intentar.');
        }

        return true;

      } catch (err) {
        let mensaje = 'No pude obtener tu ubicación. ';
        if (err.code === 1) mensaje += 'Permiso denegado por el usuario.';
        else if (err.code === 2) mensaje += 'Señal no disponible. Revisa que el GPS esté encendido.';
        else if (err.code === 3) mensaje += 'Tardó demasiado en responder.';
        await this.bot('😕 ' + mensaje + ' Usemos el link de Maps.');
        return false;
      }
    },

    // ⭐ Link de mapa con bucle de reintento (no sale hasta que sea válido)
    async pedirLinkMapa() {
      while (true) {
        await this.bot('¿Me pegas el link de Google Maps con tu ubicación?');
        await this.bot('💡 Abre Google Maps → toca tu punto azul → Compartir → copia el enlace aquí.', 500);

        const mapa = await this.esperarRespuesta({
          placeholder: 'https://maps.google.com/?q=...',
          tipo: 'texto'
        });

        if (mapa && mapa.toLowerCase().includes('http')) {
          this.form.urlMapa = mapa;
          await this.bot('✅ Ubicación registrada. 👍', 300);
          return;
        }

        await this.bot('⚠️ El enlace debe empezar con <b>https://</b> — intenta de nuevo.');
      }
    },

    async reverseGeocoding(lat, lng) {
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=es`;
      const res = await fetch(url, {
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) return '';
      const data = await res.json();
      return data?.display_name || '';
    },

    async pedirNombre() {
      const v = await this.esperarRespuesta({ placeholder: 'Ej: Juan', tipo: 'texto' });
      if (v.length < 2) {
        await this.bot('Escribe tu nombre por favor.');
        return this.pedirNombre();
      }
      return this.capitalizar(v);
    },
    async pedirApellido() {
      const v = await this.esperarRespuesta({ placeholder: 'Ej: Pérez', tipo: 'texto' });
      if (v.length < 2) {
        await this.bot('Escribe tu apellido por favor.');
        return this.pedirApellido();
      }
      return this.capitalizar(v);
    },
    async pedirCedula() {
      const v = await this.esperarRespuesta({ placeholder: 'Ej: V-12345678', tipo: 'texto' });
      if (v.replace(/\D/g, '').length < 6) {
        await this.bot('La cédula debe tener al menos 6 números. Intenta de nuevo.');
        return this.pedirCedula();
      }
      return this.formatearCedula(v);
    },
    async pedirTelefono() {
      const v = await this.esperarRespuesta({ placeholder: 'Ej: 0416-1234567', tipo: 'texto' });
      if (v.replace(/\D/g, '').length < 10) {
        await this.bot('El teléfono debe tener al menos 10 dígitos. Intenta de nuevo.');
        return this.pedirTelefono();
      }
      return this.formatearTelefono(v);
    },
    async pedirEmail() {
      const v = await this.esperarRespuesta({ placeholder: 'Ej: juan@correo.com', tipo: 'texto' });
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        await this.bot('Ese correo no parece válido. Intenta de nuevo.');
        return this.pedirEmail();
      }
      return v.toLowerCase();
    },

    async pedirServicios() {
      if (!this.tiposRecarga.length) {
        await this.bot('😕 No hay servicios disponibles en este momento.');
        return;
      }

      await this.bot('Estos son los servicios que tenemos hoy 👇');
      this.carrito = [];

      while (true) {
        const botonesServicios = this.tiposRecarga.map((t) => ({
          label: t.nombre + ' (' + this.formatearNumero(t.kilogramos, 0) + ' kg) — Bs. ' + this.formatearNumero(t.precioBs, 2),
          value: 'add:' + t.idTipoRecarga
        }));
        botonesServicios.push({ label: '✅ Terminar pedido', value: 'fin' });

        const r = await this.esperarRespuesta({
          botones: botonesServicios,
          tipo: 'botones'
        });

        if (r === 'fin') {
          if (!this.carrito.length) {
            await this.bot('😕 Aún no has agregado ninguna bombona. Elige al menos una por favor.');
            continue;
          }
          break;
        }

        if (typeof r === 'string' && r.startsWith('add:')) {
          const id = r.replace('add:', '');
          const t = this.tiposRecarga.find((x) => String(x.idTipoRecarga) === String(id));
          if (!t) continue;

          await this.bot('¿Cuántas de <b>' + t.nombre + '</b> quieres?');
          const cantResp = await this.esperarRespuesta({
            placeholder: 'Escribe un número (ej: 3)',
            tipo: 'texto'
          });

          const numParsed = parseInt(cantResp, 10);
          if (isNaN(numParsed) || numParsed < 1) {
            await this.bot('😕 No entendí. Por favor escribe un número mayor a 0.');
            continue;
          }
          const cantidad = Math.min(99, numParsed);
          if (numParsed > 99) {
            await this.bot('⚠️ El máximo por servicio es 99. Te agrego 99.');
          }

          const existente = this.carrito.find((i) => i.idTipoRecarga === t.idTipoRecarga);
          if (existente) {
            existente.cantidad += cantidad;
          } else {
            this.carrito.push({
              idTipoRecarga: t.idTipoRecarga,
              nombre: t.nombre,
              codigo: t.codigo,
              kilogramos: t.kilogramos,
              litrosEquivalente: t.litrosEquivalente,
              precioBs: Number(t.precioBs || 0),
              cantidad: cantidad
            });
          }

          const resumen = this.carrito
            .map((i) => '• ' + i.cantidad + ' × ' + i.nombre + ' = Bs. ' + this.formatearNumero(i.precioBs * i.cantidad, 2))
            .join('\n');
          await this.bot(
            '✅ Agregado: <b>' + cantidad + ' × ' + t.nombre + '</b>\n\n' +
            '<b>Tu pedido hasta ahora:</b>\n' + resumen +
            '\n\n<b>Total: Bs. ' + this.formatearNumero(this.totalGeneralBs, 2) + '</b>\n\n' +
            '¿Quieres agregar más o terminamos?'
          );
        }
      }

      const resumen = this.carrito
        .map((i) => '• ' + i.cantidad + ' × ' + i.nombre + ' = Bs. ' + this.formatearNumero(i.precioBs * i.cantidad, 2))
        .join('\n');
      await this.bot(
        '<b>🛒 Pedido final:</b>\n' + resumen +
        '\n\n<b>💰 Total: Bs. ' + this.formatearNumero(this.totalGeneralBs, 2) + '</b>'
      );

      const conf = await this.esperarRespuesta({
        botones: [
          { label: '✅ Sí, está bien', value: 'ok' },
          { label: '🔁 Cambiar pedido', value: 'again' }
        ],
        tipo: 'botones'
      });
      if (conf === 'again') {
        this.carrito = [];
        return this.pedirServicios();
      }
    },

    async confirmarYEnviar() {
      await this.bot('📋 Reviso todo antes de enviar…', 500);

      // ⭐ Validación final de ubicación antes de mostrar el resumen
      if (!this.form.urlMapa || !String(this.form.urlMapa).toLowerCase().includes('http')) {
        await this.bot('⚠️ Falta la ubicación en el mapa. Es obligatoria para la entrega.');
        await this.pedirUbicacionObligatoria();
      }

      await this.bot(
        '<b>Resumen:</b>\n' +
        '👤 ' + this.form.nombre + ' ' + this.form.apellido + '\n' +
        '🆔 ' + this.form.cedula + '\n' +
        '📞 ' + this.form.telefono + '\n' +
        '📧 ' + this.form.email + '\n' +
        '📍 ' + this.form.direccion + '\n' +
        (this.form.puntoReferencia ? '📌 Ref: ' + this.form.puntoReferencia + '\n' : '') +
        '🗺️ Mapa: ' + this.form.urlMapa + '\n' +
        '🚚 ' + this.carrito.map((i) => i.cantidad + '×' + i.nombre).join(', ') + '\n' +
        '<b>💰 Total: Bs. ' + this.formatearNumero(this.totalGeneralBs, 2) + '</b>'
      );

      const conf = await this.esperarRespuesta({
        botones: [
          { label: '✅ Confirmar pedido', value: 'si' },
          { label: '❌ Cancelar', value: 'no' }
        ],
        tipo: 'botones'
      });
      if (conf === 'no') {
        await this.bot('Pedido cancelado. 👋');
        return;
      }
      await this.enviarPedido();
    },

    async enviarPedido() {
      if (this.saving) return;
      this.saving = true;
      await this.bot('📤 Enviando tu pedido…', 600);

      try {
        const { apiFetch } = await import('@/api/index.js');
        const servicios = this.carrito.map((i) => ({
          idTipoRecarga: i.idTipoRecarga,
          cantidad: i.cantidad
        }));

        const cedulaLimpia = String(this.form.cedula || '').replace(/\D/g, '');

        let endpoint, payload;
        if (this.modo === 'nuevo') {
          endpoint = '/api/ClienteGas/registro-web';
          payload = {
            nombre: this.form.nombre.trim(),
            apellido: this.form.apellido.trim(),
            cedula: cedulaLimpia,
            telefono: this.form.telefono.trim(),
            email: this.form.email.trim().toLowerCase(),
            direccion: this.form.direccion.trim(),
            urlMapa: this.form.urlMapa.trim(),
            puntoReferencia: this.form.puntoReferencia?.trim() || null,
            tipoCliente: this.form.tipoCliente,
            servicios
          };
        } else {
          endpoint = '/api/ClienteGas/pedido-web';
          payload = {
            cedula: cedulaLimpia,
            telefono: this.form.telefono.trim(),
            email: this.form.email.trim().toLowerCase(),
            direccion: this.form.direccion.trim(),
            urlMapa: this.form.urlMapa.trim(),
            puntoReferencia: this.form.puntoReferencia?.trim() || null,
            tipoCliente: this.form.tipoCliente,
            servicios
          };
        }

        const response = await apiFetch(endpoint, {
          method: 'POST',
          body: JSON.stringify(payload)
        });

        if (!response) {
          await this.bot('⚠️ No se recibió respuesta del servidor.');
          return;
        }
        if (response.success === false) {
          await this.bot('⚠️ ' + (response.error || 'Error al procesar.'));
          return;
        }

        const numeroPedido =
          response?.data?.pedido?.numeroPedido ||
          response?.data?.pedido?.numero_pedido ||
          response?.data?.numeroPedido ||
          response?.data?.numero_pedido ||
          response?.pedido?.numeroPedido ||
          response?.pedido?.numero_pedido ||
          response?.numeroPedido ||
          response?.numero_pedido ||
          null;

        const codigoEntrega =
          response?.data?.pedido?.codigoEntrega ||
          response?.data?.pedido?.codigo_entrega ||
          response?.data?.cliente?.codigoEntrega ||
          response?.data?.cliente?.codigo_entrega ||
          response?.data?.codigoEntrega ||
          response?.pedido?.codigoEntrega ||
          response?.cliente?.codigoEntrega ||
          response?.codigoEntrega ||
          null;

        const estadoPedido =
          response?.data?.pedido?.estado ||
          response?.data?.estado ||
          null;

        const idPedidoRecarga =
          response?.data?.pedido?.idPedidoRecarga ||
          response?.data?.pedido?.id_pedido_recarga ||
          response?.data?.idPedidoRecarga ||
          response?.data?.id_pedido_recarga ||
          response?.pedido?.idPedidoRecarga ||
          response?.idPedidoRecarga ||
          null;

        const nombreZona =
          this.clienteEncontrado?.nombreZona ||
          this.clienteEncontrado?.NombreZona ||
          this.clienteEncontrado?.zona ||
          response?.data?.pedido?.nombreZona ||
          null;

        await this.bot(
          '✅ <b>¡Pedido ' + (numeroPedido ? 'EG-' + numeroPedido : 'confirmado') + '!</b>',
          900
        );

        if (codigoEntrega) {
          const codigoFmt = String(codigoEntrega).padStart(6, '0');
          await this.bot(
            '🔐 <b>CÓDIGO DE ENTREGA</b>\n' +
            '<span style="font-size:1.6em;font-weight:900;letter-spacing:4px;color:#E87A30">' + codigoFmt + '</span>\n\n' +
            '⚠️ <b>Dáselo al delivery solo al recibir tu gas.</b>',
            800
          );
        }

        const resumen = this.carrito
          .map((i) => '• ' + i.cantidad + ' × ' + i.nombre + ' = Bs. ' + this.formatearNumero(i.precioBs * i.cantidad, 2))
          .join('\n');
        await this.bot(
          '🛒 <b>Resumen del pedido:</b>\n' + resumen +
          '\n\n💰 <b>Total: Bs. ' + this.formatearNumero(this.totalGeneralBs, 2) + '</b>\n' +
          '🚚 Llegada estimada: 45–60 min.\n' +
          '🔄 Ten lista tu bombona vacía para el cambio.',
          700
        );

        if (nombreZona) {
          await this.bot('📍 Tu pedido va a la zona <b>' + nombreZona + '</b>.', 600);
        }

        await this.bot(
          '📋 <b>Datos registrados:</b>\n' +
          '👤 ' + this.form.nombre + ' ' + this.form.apellido + '\n' +
          '🆔 ' + this.form.cedula + '\n' +
          '📞 ' + this.form.telefono + '\n' +
          '📍 ' + this.form.direccion,
          600
        );

        const estadosValidos = ['CONFIRMADO', 'Confirmado', 'PAGO_PENDIENTE'];
        const puedeReportar = estadosValidos.includes(estadoPedido) && !!idPedidoRecarga;

        if (puedeReportar) {
          await this.bot(
            '💰 <b>¡Acción requerida!</b>\n' +
            'Tu pedido está <b>CONFIRMADO</b>.\n' +
            'Ahora reporta tu pago móvil y un operador lo verificará.',
            700
          );

          const r = await this.esperarRespuesta({
            botones: [
              { label: '📄 Reportar mi pago ahora', value: 'reportar' },
              { label: '⏳ Lo haré después', value: 'despues' }
            ],
            tipo: 'botones'
          });

          if (r === 'reportar') {
            await this.bot('✅ Perfecto, te llevo al formulario de pago…', 500);
            setTimeout(() => {
              this.cerrar();
              this.$router.push({
                path: '/gas/reportar-pago',
                query: { pedido: idPedidoRecarga }
              });
            }, 1500);
            return;
          }
        } else {
          await this.bot(
            '⏳ <b>Próximo paso</b>\n' +
            'Un operador revisará tu pedido y te contactará al teléfono\n' +
            '<b>' + this.form.telefono + '</b>\n' +
            'para coordinar la entrega. 🙌',
            800
          );
        }

        await this.bot('¡Gracias por tu pedido! 🔥\n¿Necesitas algo más?', 700);

        const fin = await this.esperarRespuesta({
          botones: [
            { label: '🛒 Hacer otro pedido', value: 'otro' },
            { label: '👋 Cerrar', value: 'cerrar' }
          ],
          tipo: 'botones'
        });

        if (fin === 'otro') {
          await this.reiniciar();
        } else {
          this.toast.success('¡Pedido enviado!');
          setTimeout(() => this.cerrar(), 800);
        }

      } catch (error) {
        console.error('💥 Error en envío:', error);
        await this.bot('⚠️ ' + (error?.message || 'Error al procesar.'));
        this.toast.error(error?.message || 'Error al procesar');
      } finally {
        this.saving = false;
      }
    },

    formatearCedula(v) {
      const valor = (v || '').toUpperCase().replace(/[^VEJPG0-9]/g, '');
      const letra = (valor.match(/^[VEJPG]/) || ['V'])[0];
      const num = valor.replace(/[^0-9]/g, '');
      return num ? letra + '-' + num : letra;
    },
    formatearTelefono(v) {
      let val = (v || '').replace(/[^0-9]/g, '');
      if (val.length > 11) val = val.substring(0, 11);
      if (val.length > 4) return val.substring(0, 4) + '-' + val.substring(4);
      return val;
    },
    capitalizar(s) {
      return (s || '').charAt(0).toUpperCase() + (s || '').slice(1).toLowerCase();
    },
    formatearNumero(v, dec = 2) {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      if (isNaN(n)) return '0';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: dec,
        maximumFractionDigits: dec
      }).format(n);
    }
  }
};
</script>

<!-- ⚠️ SIN "scoped" — para que aplique a los elementos creados con appendChild -->
<style>
/* ═══════════════════════════════════════════════════════
   OVERLAY
═══════════════════════════════════════════════════════ */
.cm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background: rgba(15, 26, 47, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow: hidden;
}

/* ═══════════════════════════════════════════════════════
   MODAL
═══════════════════════════════════════════════════════ */
.cm-modal {
  width: 100%;
  max-width: 480px;
  height: calc(100vh - 2rem);
  height: calc(100dvh - 2rem);
  max-height: 780px;
  background: #ECE5DD;
  border-radius: 24px;
  border: 6px solid #0f1a2f;
  box-shadow:
    0 30px 80px -20px rgba(15, 26, 47, 0.7),
    0 0 0 1px rgba(255, 255, 255, 0.08) inset;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

/* ═══════════════════════════════════════════════════════
   HEADER
═══════════════════════════════════════════════════════ */
.cm-header {
  background: #0f1a2f;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(232, 122, 48, 0.15);
}

.cm-header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.cm-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #fff;
  display: grid;
  place-items: center;
  flex: none;
  overflow: hidden;
}
.cm-avatar img { height: 28px; width: auto; object-fit: contain; }

.cm-header-text { min-width: 0; }
.cm-header-text strong {
  font-size: 0.95rem;
  font-weight: 900;
  display: block;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cm-header-text small { font-size: 0.7rem; opacity: 0.8; }

.cm-header-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.cm-icon-btn {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
  font-size: 1.1rem;
}
.cm-icon-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.05);
}
.cm-icon-btn.cm-close:hover {
  background: #ef4444;
}

/* ═══════════════════════════════════════════════════════
   BODY
═══════════════════════════════════════════════════════ */
.cm-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* ═══════════════════════════════════════════════════════
   CHAT
═══════════════════════════════════════════════════════ */
.cm-chat {
  flex: 1;
  overflow-y: auto;
  padding: 18px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: #1B2433;
  background-color: #EFE9E1;
  background-image:
    radial-gradient(circle at 15% 25%, rgba(232, 122, 48, 0.06) 0 3px, transparent 3px),
    radial-gradient(circle at 75% 55%, rgba(93, 173, 226, 0.05) 0 2px, transparent 2px),
    radial-gradient(circle at 45% 85%, rgba(26, 42, 74, 0.04) 0 2.5px, transparent 2.5px),
    radial-gradient(circle at 88% 12%, rgba(232, 122, 48, 0.05) 0 2px, transparent 2px),
    radial-gradient(circle at 8% 72%, rgba(93, 173, 226, 0.04) 0 2.5px, transparent 2.5px),
    linear-gradient(180deg, #F2ECE4 0%, #EAE3DA 100%);
  background-size: 220px 220px, 160px 160px, 240px 240px, 200px 200px, 180px 180px, 100% 100%;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

.cm-chat::-webkit-scrollbar { width: 6px; }
.cm-chat::-webkit-scrollbar-track { background: transparent; }
.cm-chat::-webkit-scrollbar-thumb {
  background: rgba(26, 42, 74, 0.15);
  border-radius: 3px;
}
.cm-chat::-webkit-scrollbar-thumb:hover { background: rgba(26, 42, 74, 0.3); }

/* ═══════════════════════════════════════════════════════
   BURBUJAS
═══════════════════════════════════════════════════════ */
.cm-msg {
  max-width: 82%;
  padding: 11px 15px;
  border-radius: 16px;
  font-size: 0.875rem;
  line-height: 1.5;
  position: relative;
  white-space: pre-line;
  word-wrap: break-word;
  font-weight: 500;
  letter-spacing: 0.005em;
  transition: transform 0.2s ease;
}

.cm-msg.in {
  background: linear-gradient(180deg, #FFFFFF 0%, #FBFBFB 100%);
  color: #1B2433;
  align-self: flex-start;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
  border-bottom-left-radius: 6px;
  box-shadow:
    0 1px 2px rgba(15, 26, 47, 0.06),
    0 4px 12px rgba(15, 26, 47, 0.06),
    0 0 0 1px rgba(15, 26, 47, 0.03);
  animation: cmSlideInLeft 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.cm-msg.out {
  background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
  color: #FFFFFF;
  align-self: flex-end;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 6px;
  box-shadow:
    0 2px 6px rgba(232, 122, 48, 0.28),
    0 8px 20px rgba(232, 122, 48, 0.22),
    0 0 0 1px rgba(232, 122, 48, 0.15) inset;
  animation: cmSlideInRight 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.cm-msg b {
  font-weight: 800;
  letter-spacing: 0.01em;
}

.cm-msg.in b { color: #1a2a4a; }
.cm-msg.out b { color: #FFFFFF; }

@keyframes cmSlideInLeft {
  from { opacity: 0; transform: translateX(-12px) scale(0.96); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
}

@keyframes cmSlideInRight {
  from { opacity: 0; transform: translateX(12px) scale(0.96); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
}

/* ═══════════════════════════════════════════════════════
   TYPING
═══════════════════════════════════════════════════════ */
.cm-typing {
  align-self: flex-start;
  background: linear-gradient(180deg, #FFFFFF 0%, #FBFBFB 100%);
  border-radius: 16px;
  border-bottom-left-radius: 6px;
  padding: 14px 18px;
  display: flex;
  gap: 6px;
  align-items: center;
  box-shadow:
    0 1px 2px rgba(15, 26, 47, 0.06),
    0 4px 12px rgba(15, 26, 47, 0.06),
    0 0 0 1px rgba(15, 26, 47, 0.03);
  animation: cmSlideInLeft 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.cm-typing i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
  animation: cmBounce 1.4s infinite ease-in-out;
  box-shadow: 0 1px 3px rgba(232, 122, 48, 0.3);
}
.cm-typing i:nth-child(2) { animation-delay: 0.15s; }
.cm-typing i:nth-child(3) { animation-delay: 0.3s; }

@keyframes cmBounce {
  0%, 60%, 100% { transform: translateY(0) scale(1); opacity: 0.5; }
  30% { transform: translateY(-6px) scale(1.15); opacity: 1; }
}

/* ═══════════════════════════════════════════════════════
   OPTIONS
═══════════════════════════════════════════════════════ */
.cm-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px 16px;
  background: linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(240, 236, 228, 0.98) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-top: 1px solid rgba(26, 42, 74, 0.06);
  flex-shrink: 0;
  max-height: 50%;
  overflow-y: auto;
  animation: cmFadeUp 0.3s ease-out;
}

.cm-options::-webkit-scrollbar { width: 5px; }
.cm-options::-webkit-scrollbar-thumb {
  background: rgba(26, 42, 74, 0.15);
  border-radius: 3px;
}

.cm-option-btn {
  width: 100%;
  background: linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%);
  color: #1a2a4a;
  border: 1.5px solid rgba(93, 173, 226, 0.25);
  border-radius: 14px;
  padding: 14px 18px;
  font: 700 0.9rem 'Lexend', sans-serif;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  text-align: center;
  box-shadow:
    0 1px 2px rgba(15, 26, 47, 0.04),
    0 2px 8px rgba(15, 26, 47, 0.04);
  letter-spacing: 0.01em;
}

.cm-option-btn:hover {
  background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
  border-color: transparent;
  color: #FFFFFF;
  transform: translateY(-2px);
  box-shadow:
    0 4px 12px rgba(232, 122, 48, 0.3),
    0 12px 28px rgba(232, 122, 48, 0.25);
}

.cm-option-btn:active {
  transform: translateY(0) scale(0.98);
}

.cm-options-inline {
  flex-direction: row !important;
  flex-wrap: wrap;
  padding: 10px 12px 6px;
  max-height: none;
}
.cm-options-inline .cm-option-inline {
  width: auto;
  padding: 9px 16px;
  border-radius: 999px;
  font-size: 0.8rem;
}

@keyframes cmFadeUp {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ═══════════════════════════════════════════════════════
   INPUT BAR
═══════════════════════════════════════════════════════ */
.cm-input-bar {
  display: flex;
  gap: 8px;
  padding: 12px 14px;
  background: linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248, 245, 240, 0.98) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  align-items: center;
  flex-shrink: 0;
  border-top: 1px solid rgba(26, 42, 74, 0.06);
  box-shadow: 0 -2px 12px rgba(15, 26, 47, 0.04);
}

.cm-input-txt {
  flex: 1;
  background: #FFFFFF;
  border: 1.5px solid rgba(26, 42, 74, 0.08);
  outline: 0;
  border-radius: 22px;
  padding: 12px 18px;
  font: 500 0.9rem 'Lexend', sans-serif;
  color: #1B2433;
  box-shadow:
    0 1px 2px rgba(15, 26, 47, 0.04) inset,
    0 1px 3px rgba(15, 26, 47, 0.03);
  transition: all 0.2s ease;
}

.cm-input-txt::placeholder {
  color: #94a3b8;
  font-weight: 400;
}

.cm-input-txt:focus {
  border-color: rgba(232, 122, 48, 0.4);
  box-shadow:
    0 0 0 3px rgba(232, 122, 48, 0.1),
    0 1px 3px rgba(15, 26, 47, 0.05);
}

.cm-input-txt:disabled {
  background: #f1f5f9;
  cursor: not-allowed;
  opacity: 0.7;
}

.cm-input-send {
  width: 46px;
  height: 46px;
  border: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
  color: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  box-shadow:
    0 2px 6px rgba(232, 122, 48, 0.3),
    0 6px 16px rgba(232, 122, 48, 0.25);
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  font-size: 1.1rem;
}

.cm-input-send:hover:not(:disabled) {
  transform: scale(1.08) rotate(-8deg);
  box-shadow:
    0 4px 10px rgba(232, 122, 48, 0.4),
    0 10px 24px rgba(232, 122, 48, 0.35);
}

.cm-input-send:active:not(:disabled) {
  transform: scale(0.95);
}

.cm-input-send:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  box-shadow: 0 2px 6px rgba(232, 122, 48, 0.15);
}

/* ═══════════════════════════════════════════════════════
   TRANSICIÓN
═══════════════════════════════════════════════════════ */
.cm-slide-enter-active {
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
              opacity 0.25s ease;
}
.cm-slide-leave-active {
  transition: transform 0.25s ease, opacity 0.2s ease;
}
.cm-slide-enter-from {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
}
.cm-slide-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.98);
}

/* ═══════════════════════════════════════════════════════
   MÓVIL
═══════════════════════════════════════════════════════ */
@media (max-width: 640px) {
  .cm-overlay {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }

  .cm-modal {
    max-width: 100%;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
    border: 0;
    box-shadow: none;
  }

  .cm-header {
    padding: 10px 12px;
    padding-top: max(10px, env(safe-area-inset-top));
  }
  .cm-avatar { width: 34px; height: 34px; }
  .cm-avatar img { height: 24px; }
  .cm-header-text strong { font-size: 0.88rem; }
  .cm-header-text small { font-size: 0.65rem; }
  .cm-icon-btn { width: 32px; height: 32px; }

  .cm-chat { padding: 14px 10px; gap: 8px; }
  .cm-msg {
    max-width: 88%;
    padding: 10px 14px;
    font-size: 0.86rem;
    line-height: 1.48;
  }

  .cm-options {
    padding: 10px 12px 14px;
    padding-bottom: max(14px, env(safe-area-inset-bottom));
    gap: 8px;
    max-height: 45%;
  }
  .cm-option-btn {
    padding: 13px 16px;
    font-size: 0.86rem;
    min-height: 48px;
    -webkit-tap-highlight-color: transparent;
  }

  .cm-input-bar {
    padding: 10px 12px;
    padding-bottom: max(10px, env(safe-area-inset-bottom));
    gap: 6px;
  }
  .cm-input-txt {
    font-size: 16px;
    padding: 11px 16px;
    border-radius: 20px;
  }
  .cm-input-send {
    width: 44px;
    height: 44px;
  }
}

@media (max-width: 380px) {
  .cm-msg { font-size: 0.83rem; padding: 9px 12px; }
  .cm-option-btn { font-size: 0.83rem; padding: 12px 14px; min-height: 46px; }
  .cm-input-send { width: 42px; height: 42px; }
}

@media (max-width: 900px) and (orientation: landscape) {
  .cm-modal {
    height: 100dvh;
  }
  .cm-options { max-height: 40%; }
}
</style>